/**
 * API helpers for the server-side Microsoft Graph OAuth flow.
 * These call our backend endpoints, NOT Microsoft directly.
 */
import axios from "axios";
import { API } from "./client";

/** Check the user's server-side Microsoft Graph connection state. */
export async function getMsStatus() {
  const res = await axios.get(`${API}/auth/microsoft/status`);
  return res.data; // { connected: bool, needs_reauth: bool }
}

/**
 * Start the OAuth2 PKCE flow.
 * Returns the Microsoft authorization URL — caller should redirect to it.
 */
export async function getMsAuthorizeUrl() {
  const res = await axios.get(`${API}/auth/microsoft/authorize`);
  return res.data.auth_url;
}

/** Remove the server-side Microsoft token for the current user. */
export async function disconnectMs() {
  const res = await axios.delete(`${API}/auth/microsoft/disconnect`);
  return res.data;
}

// ── OneDrive proxy helpers ────────────────────────────────────────────────────

/** Return the user's top-level OneDrive folder. */
export async function getDriveRoot() {
  const res = await axios.get(`${API}/onedrive/drive-root`);
  return res.data;
}

export async function listFolder(folderId) {
  const res = await axios.get(`${API}/onedrive/ls`, { params: { folder_id: folderId } });
  const items = res.data?.items;
  if (!Array.isArray(items)) {
    throw new Error("Could not read the OneDrive folder contents.");
  }
  return items;
}

/**
 * For a list of drawing item IDs, return only those with archived previous versions.
 * Response: { "<drawing_id>": { archive_folder_id, archive_folder_url, count } }
 */
export async function checkPreviousVersions(drawingIds) {
  if (!drawingIds || drawingIds.length === 0) return {};
  const res = await axios.post(`${API}/onedrive/check-previous-versions`, {
    drawing_ids: drawingIds,
  });
  return res.data;
}

/**
 * Is a given folder the "previous versions" archive folder for a tracked drawing?
 * Returns { is_archive_folder: bool, current_drawing_id?, current_drawing_name?, project_id? }
 */
export async function getArchiveFolderContext(folderId) {
  const res = await axios.get(`${API}/onedrive/archive-folder/${folderId}`);
  return res.data;
}

/**
 * Restore an archived drawing as the current version of its drawing.
 * Body: { archived_item_id }
 */
export async function restoreDrawingVersion(archivedItemId) {
  const res = await axios.post(`${API}/onedrive/drawings/restore-version`, {
    archived_item_id: archivedItemId,
  });
  return res.data;
}

export async function getDdRoot() {
  const res = await axios.get(`${API}/onedrive/root`);
  return res.data;
}

export async function createFolder(parentId, folderName) {
  const res = await axios.post(`${API}/onedrive/folder`, {
    parent_id: parentId, folder_name: folderName,
  });
  return res.data;
}

export async function createProjectFolder(projectName) {
  const res = await axios.post(`${API}/onedrive/project-folder`, {
    project_name: projectName,
  });
  return res.data;
}

export async function uploadFile(folderId, file, projectId = null) {
  const form = new FormData();
  form.append("file", file);
  const res = await axios.put(`${API}/onedrive/upload`, form, {
    params: {
      folder_id: folderId,
      ...(projectId ? { project_id: projectId } : {}),
    },
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function downloadFileItem(itemId) {
  return axios.get(`${API}/onedrive/download/${itemId}`, {
    responseType: "blob",
  });
}

export async function getEmailFileContent(itemId) {
  const res = await axios.get(`${API}/onedrive/item/${itemId}/email-content`);
  return res.data; // { html, filename }
}

export async function renameItem(itemId, newName) {
  const res = await axios.patch(`${API}/onedrive/rename/${itemId}`, {
    new_name: newName,
  });
  return res.data;
}

export async function moveItem(itemId, destinationFolderId) {
  const res = await axios.patch(`${API}/onedrive/move/${itemId}`, {
    destination_folder_id: destinationFolderId,
  });
  return res.data;
}

export async function deleteItem(itemId) {
  const res = await axios.delete(`${API}/onedrive/item/${itemId}`);
  return res.data;
}

/**
 * Smart-upload a single file to a project.
 * The backend classifies it (AI vision) and routes it to drawings/ or project root.
 * Returns { filename, classification, saved_to, saved_to_url, matched, matched_url, error }
 */
export async function smartUploadFile(projectId, file) {
  const form = new FormData();
  form.append("project_id", projectId);
  form.append("file", file);
  const res = await axios.post(`${API}/onedrive/smart-upload`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000, // 3 min per file — AI classification + vision can be slow
  });
  return res.data;
}
